import React from 'react';
import { PropertyCardProps } from '../../types/expertise-detail.types';
import { useFacadeImage } from '../../hooks/useFacadeImage';
import { formatCurrency, formatAddress } from '../../utils/formatters';
import { Loading } from '@/ui/components/atoms/Loading';

export const PropertyCard: React.FC<PropertyCardProps> = ({
  properties,
  affaireId,
  loading,
}) => {
  const { imageUrl, loading: imageLoading, error: imageError, retry } = useFacadeImage(affaireId);
  
  if (loading) {
    return (
      <div className="property-card">
        <Loading size="medium" text="Chargement des informations de propriété..." />
      </div>
    );
  }

  if (!properties || properties.length === 0) {
    return (
      <div className="property-card empty">
        <p>Aucune propriété associée à cette expertise</p>
      </div>
    );
  }

  const primaryProperty = properties[0];
  const hasMultipleProperties = properties.length > 1;

  return (
    <div className="property-facade-section">
        {/* Facade Image Section */}
        <div className="facade-section">
          <div className="facade-image-wrapper">
            {imageLoading ? (
              <Loading size="small" text="Chargement..." />
            ) : imageError ? (
              <div className="image-error">
                <svg className="error-icon" width="32" height="32" viewBox="0 0 48 48" fill="none">
                  <path d="M24 4L4 40h40L24 4z" stroke="#9CA3AF" strokeWidth="2" fill="none"/>
                  <circle cx="24" cy="34" r="2" fill="#9CA3AF"/>
                  <path d="M24 18v10" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <button onClick={retry} className="retry-button">
                  Réessayer
                </button>
              </div>
            ) : imageUrl ? (
              <img 
                src={imageUrl} 
                alt="Façade de la propriété" 
                className="facade-image"
                onError={() => retry()}
              />
            ) : (
              <div className="no-image">
                <svg className="placeholder-icon" width="40" height="40" viewBox="0 0 64 64" fill="none">
                  <rect width="64" height="64" rx="8" fill="#F3F4F6"/>
                  <path d="M20 44L32 32L44 44M28 28C28 30.2091 26.2091 32 24 32C21.7909 32 20 30.2091 20 28C20 25.7909 21.7909 24 24 24C26.2091 24 28 25.7909 28 28Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Property Information Elements - No Card Wrapper */}
        <div className="property-info-content">
          {/* Header with Type and Location */}
          <div className="property-header">
            <div className="property-badge">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="property-type-text">{primaryProperty.type_bien_unifie || 'Non défini'}</span>
              {primaryProperty.type_projet && (
                <span className="property-subtype">{primaryProperty.type_projet}</span>
              )}
            </div>
          </div>

          {/* Location Bar */}
          <div className="location-bar">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <span className="location-text">{formatAddress(
              primaryProperty.address.street,
              undefined,
              primaryProperty.address.postcode,
              primaryProperty.address.locality
            )}</span>
          </div>

          {/* Info Cards Grid - Now standalone cards */}
          <div className="info-grid">
            {/* Surface Card */}
            <div className="info-tile">
              <div className="tile-header">
                <div className="tile-icon surface-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M3 9h18M9 9v12" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                </div>
                <span className="tile-label">Surfaces</span>
              </div>
              <div className="tile-content">
                {primaryProperty.surfaces.surface_habitable && (
                  <div className="metric-row">
                    <span className="metric-value">{primaryProperty.surfaces.surface_habitable}</span>
                    <span className="metric-unit">m²</span>
                    <span className="metric-label">habitable</span>
                  </div>
                )}
                {primaryProperty.surfaces.superficie_parcelle && (
                  <div className="metric-row">
                    <span className="metric-value">{primaryProperty.surfaces.superficie_parcelle}</span>
                    <span className="metric-unit">m²</span>
                    <span className="metric-label">terrain</span>
                  </div>
                )}
                {!primaryProperty.surfaces.surface_habitable && !primaryProperty.surfaces.superficie_parcelle && (
                  <div className="empty-metric">
                    <span>Non renseigné</span>
                  </div>
                )}
              </div>
            </div>

            {/* Valuation Card */}
            <div className="info-tile">
              <div className="tile-header">
                <div className="tile-icon valuation-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span className="tile-label">Valorisation</span>
              </div>
              <div className="tile-content valuation-content">
                <div className="valuation-grid">
                  {((primaryProperty as any)['expertise-price-vgg'] || primaryProperty.expertise_price_vgg) && (
                    <div className="price-item primary">
                      <span className="price-label">Vente gré à gré</span>
                      <span className="price-value">{formatCurrency((primaryProperty as any)['expertise-price-vgg'] || primaryProperty.expertise_price_vgg)}</span>
                    </div>
                  )}
                  {((primaryProperty as any)['expertise-price-vp'] || primaryProperty.expertise_price_vp) && (
                    <div className="price-item">
                      <span className="price-label">Vente forcée</span>
                      <span className="price-value">{formatCurrency((primaryProperty as any)['expertise-price-vp'] || primaryProperty.expertise_price_vp)}</span>
                    </div>
                  )}
                  {((primaryProperty as any)['expertise-price-at-vgg'] || primaryProperty.expertise_price_at_vgg) && (
                    <div className="price-item primary">
                      <span className="price-label">VGG après travaux</span>
                      <span className="price-value">{formatCurrency((primaryProperty as any)['expertise-price-at-vgg'] || primaryProperty.expertise_price_at_vgg)}</span>
                    </div>
                  )}
                  {((primaryProperty as any)['expertise-price-at-vp'] || primaryProperty.expertise_price_at_vp) && (
                    <div className="price-item">
                      <span className="price-label">VF après travaux</span>
                      <span className="price-value">{formatCurrency((primaryProperty as any)['expertise-price-at-vp'] || primaryProperty.expertise_price_at_vp)}</span>
                    </div>
                  )}
                </div>
                {!(primaryProperty as any)['expertise-price-vgg'] && !primaryProperty.expertise_price_vgg && 
                 !(primaryProperty as any)['expertise-price-vp'] && !primaryProperty.expertise_price_vp && 
                 !(primaryProperty as any)['expertise-price-at-vgg'] && !primaryProperty.expertise_price_at_vgg && 
                 !(primaryProperty as any)['expertise-price-at-vp'] && !primaryProperty.expertise_price_at_vp && (
                  <div className="empty-metric valuation-pending">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <span>En cours d'évaluation</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Additional Property Details if available */}
          {hasMultipleProperties && (
            <div className="multiple-properties-note">
              <svg className="note-icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#ef5343" strokeWidth="2"/>
                <path d="M12 8v4M12 16h.01" stroke="#ef5343" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>+{properties.length - 1} autre{properties.length > 2 ? 's' : ''} propriété{properties.length > 2 ? 's' : ''}</span>
            </div>
          )}
        </div>

      <style>{`
        /* Main container - flexible layout */
        .property-facade-section {
          display: grid;
          grid-template-columns: 380px 1fr;
          gap: 20px;
          padding: 0;
          margin: 0;
          align-items: start;
        }

        .property-facade-card {
          background: white;
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
          border: 1px solid #E5E7EB;
          margin-bottom: 20px;
        }

        /* Compact header */
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #F3F4F6;
        }

        .card-title {
          font-size: 16px;
          font-weight: 700;
          color: #002035;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .title-icon {
          font-size: 18px;
        }

        .properties-count {
          padding: 3px 8px;
          background: #F3F4F6;
          border: 1px solid #E5E7EB;
          border-radius: 10px;
          color: #c75c5c;
          font-size: 11px;
          font-weight: 600;
        }

        /* Horizontal layout for content */
        .card-content {
          display: grid;
          grid-template-columns: 280px 1fr;
          gap: 20px;
          align-items: start;
        }

        /* Compact facade section */
        .facade-section {
          width: 100%;
        }

        .facade-image-wrapper {
          aspect-ratio: 4/3;
          width: 100%;
          min-height: 300px;
          max-height: 400px;
          border-radius: 16px;
          overflow: hidden;
          background: linear-gradient(135deg, #F8F9FA 0%, #E5E7EB 100%);
          border: 1px solid #E5E7EB;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
        }

        .facade-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }

        .image-error,
        .no-image {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 6px;
          color: #9CA3AF;
          font-size: 11px;
        }

        .retry-button {
          padding: 4px 10px;
          background: #002035;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .retry-button:hover {
          background: #001528;
        }

        /* Property Info Content - No Card Wrapper */
        .property-info-content {
          display: flex;
          flex-direction: column;
          gap: 16px;
          flex: 1;
        }

        /* Property Header */
        .property-header {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .property-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          background: #002035;
          color: white;
          border-radius: 10px;
          font-weight: 600;
          font-size: 13px;
        }

        .property-badge svg {
          color: #ef5343;
        }

        .property-type-text {
          color: white;
        }

        .property-subtype {
          opacity: 0.7;
          font-size: 12px;
          padding-left: 8px;
          border-left: 1px solid rgba(255, 255, 255, 0.3);
          margin-left: 8px;
        }

        /* Location Bar */
        .location-bar {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          background: #F9FAFB;
          border-radius: 10px;
          border: 1px solid #E5E7EB;
        }

        .location-bar svg {
          color: #6B7280;
          flex-shrink: 0;
        }

        .location-text {
          font-size: 13px;
          color: #4B5563;
          font-weight: 500;
        }

        /* Info Grid */
        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-top: 8px;
        }

        /* Info Tiles - Now standalone cards */
        .info-tile {
          background: #FFFFFF;
          border: 1px solid #E5E7EB;
          border-radius: 16px;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          transition: all 0.2s ease;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
        }

        .info-tile:hover {
          border-color: #CBD5E1;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transform: translateY(-2px);
        }

        .tile-header {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .tile-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        
        .tile-icon svg {
          width: 18px;
          height: 18px;
        }

        .tile-icon.surface-icon {
          background: #EFF6FF;
          color: #2563EB;
        }

        .tile-icon.valuation-icon {
          background: #F0FDF4;
          color: #16A34A;
        }

        .tile-label {
          font-size: 12px;
          font-weight: 600;
          color: #6B7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .tile-content {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        /* Metrics */
        .metric-row {
          display: flex;
          align-items: baseline;
          gap: 4px;
          padding: 8px 10px;
          background: #F9FAFB;
          border-radius: 8px;
          border: 1px solid #E5E7EB;
        }

        .metric-value {
          font-size: 18px;
          font-weight: 700;
          color: #002035;
        }

        .metric-unit {
          font-size: 13px;
          color: #6B7280;
          font-weight: 600;
        }

        .metric-label {
          margin-left: auto;
          font-size: 11px;
          color: #9CA3AF;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          font-weight: 500;
        }

        /* Valuation Grid */
        .valuation-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }

        .price-item {
          padding: 8px 10px;
          background: #FFFFFF;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          transition: all 0.2s ease;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        
        .price-item .price-label {
          color: #002035;
          opacity: 0.7;
        }
        
        .price-item .price-value {
          color: #002035;
          opacity: 0.8;
        }

        .price-item.primary {
          background: rgba(239, 83, 67, 0.08);
          border: 1px solid #E5E7EB;
          position: relative;
        }
        
        .price-item.primary .price-label {
          color: #002035;
          font-weight: 600;
          opacity: 0.8;
        }
        
        .price-item.primary .price-value {
          color: #002035;
          font-weight: 700;
        }
        


        .price-item:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          background: #F9FAFB;
        }
        
        .price-item.primary:hover {
          background: rgba(239, 83, 67, 0.12);
          box-shadow: 0 4px 12px rgba(239, 83, 67, 0.15);
        }

        .price-label {
          font-size: 10px;
          font-weight: 600;
          text-transform: none;
          letter-spacing: 0;
          display: flex;
          align-items: center;
          line-height: 1.2;
        }

        .price-value {
          font-size: 13px;
          font-weight: 700;
          line-height: 1.2;
        }

        /* Empty States */
        .empty-metric {
          padding: 16px;
          text-align: center;
          background: #F9FAFB;
          border: 1px dashed #E5E7EB;
          border-radius: 8px;
          color: #9CA3AF;
          font-size: 11px;
        }

        .valuation-pending {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .valuation-pending svg {
          color: #ef5343;
        }

        .valuation-pending span {
          color: #6B7280;
          font-weight: 500;
        }

        /* Multiple Properties Note */
        .multiple-properties-note {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          background: #FEF2F2;
          border: 1px solid #ef5343;
          border-radius: 8px;
          margin-top: 8px;
        }

        .note-icon {
          width: 14px;
          height: 14px;
          flex-shrink: 0;
        }

        .multiple-properties-note span {
          font-size: 12px;
          color: #ef5343;
          font-weight: 600;
        }

        .skeleton-loader {
          padding: 20px;
          text-align: center;
          color: #9CA3AF;
          font-size: 13px;
        }

        /* Tablet responsive */
        @media (max-width: 1024px) {
          .property-facade-section {
            grid-template-columns: 320px 1fr;
            gap: 20px;
          }

          .facade-image-wrapper {
            height: 280px;
          }
          
          .property-info-content {
            gap: 14px;
          }

          .info-grid {
            gap: 12px;
          }
          
          .info-tile {
            padding: 14px;
          }
        }

        /* Mobile responsive */
        @media (max-width: 768px) {
          .property-facade-section {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .facade-image-wrapper {
            width: 100%;
            height: 250px;
          }
          
          .property-info-content {
            gap: 12px;
          }

          .info-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          
          .property-badge {
            font-size: 13px;
            padding: 6px 12px;
          }
          
          .info-tile {
            padding: 12px;
          }
          
          .metric-value {
            font-size: 18px;
          }
          
          .price-value {
            font-size: 14px;
          }
          
          .valuation-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Small mobile responsive */
        @media (max-width: 480px) {
          .property-facade-section {
            gap: 12px;
          }

          .facade-image-wrapper {
            height: 200px;
          }

          .property-info-content {
            gap: 10px;
          }
          
          .property-badge {
            font-size: 12px;
            padding: 5px 10px;
          }
          
          .property-badge svg {
            width: 16px;
            height: 16px;
          }

          .location-bar {
            padding: 10px 12px;
          }

          .location-text {
            font-size: 12px;
          }

          .info-tile {
            padding: 10px;
          }
          
          .tile-icon {
            width: 32px;
            height: 32px;
          }
          
          .tile-icon svg {
            width: 18px;
            height: 18px;
          }

          .metric-value {
            font-size: 16px;
          }

          .price-value {
            font-size: 12px;
          }
          
          .valuation-grid {
            grid-template-columns: 1fr;
            gap: 6px;
          }
          
          .price-item {
            padding: 6px 8px;
          }
        }

        /* Ultra small devices */
        @media (max-width: 360px) {

          .facade-image-wrapper {
            height: 180px;
          }

          .badge-item {
            font-size: 10px;
            padding: 4px 8px;
          }

          .badge-icon {
            width: 14px;
            height: 14px;
          }
        }
      `}</style>
    </div>
  );
};